import { useEffect, useRef, useState } from 'react';
import { IconCaptionSwitch } from '../../../icons';
import { PlayerControlButton } from '../../Player/styles';
import UsePlayerProps from '../../../Store/usePlayerProps';
import theme from '../../../theme';
import IconCaptionOff from '../../../icons/IconCaptionOff';

const ControlBarCaptionSwitch = ({ player }: { player: HTMLVideoElement }) => {
  const [captionState, setCaptionState] = useState<'on' | 'off'>('off');
  const prevCaptionIndex = UsePlayerProps((state) => state.playerState.captionState.prevCaptionIndex);
  const setPlayerState = UsePlayerProps((state) => state.setPlayerState);
  const playerState = UsePlayerProps((state) => state.playerState);
  const [isMobile, setIsMobile] = useState(false);
  const captions = playerState.sources?.captionFiles;

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCaptionState(playerState.captionState.selectedCaptionLang.lang === 'off' ? 'off' : 'on');
  }, [playerState.captionState.selectedCaptionLang.lang]);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const handleCaptionChange = () => {
    const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    setCaptionState(() => (captionState === 'off' ? 'on' : 'off'));

    if (captionState === 'on') {
      Array.from(player.textTracks).forEach((t) => {
        t.mode = 'disabled';
      });
      setPlayerState({
        ...playerState,
        captionState: {
          prevCaptionIndex,
          selectedCaptionLang: {
            lang: 'off',
            label: 'خاموش',
          },
        },
      });
    } else {
      const tracks = Array.from(player.textTracks);
      const captionsData = captions || [];
      // On iOS captions render natively ('showing'); elsewhere they render into the
      // custom #custom-track div, so the track must be 'hidden' to avoid a duplicate
      // native render (e.g. Chrome on Windows showing both languages at once).
      const activeMode = IS_IOS ? 'showing' : 'hidden';

      // textTracks excludes the "off" entry, so match by language rather than by the
      // captionFiles index. Restore the previously selected track if still valid; otherwise
      // honour the caption flagged as default, falling back to the first available track.
      const defaultCaption = captionsData.find((c) => c.default && c.language !== 'off');
      const defaultTrackIndex = defaultCaption
        ? tracks.findIndex((t) => t.language === defaultCaption.srclang || t.language === defaultCaption.language)
        : -1;

      let trackIndex = prevCaptionIndex >= 0 && prevCaptionIndex < tracks.length ? prevCaptionIndex : -1;
      if (trackIndex < 0) trackIndex = defaultTrackIndex;
      if (trackIndex < 0) trackIndex = 0;

      tracks.forEach((t, i) => {
        t.mode = i === trackIndex ? activeMode : 'disabled';
      });

      const activeTrack = tracks[trackIndex];
      const matched = captionsData.find((c) => c.srclang === activeTrack?.language || c.language === activeTrack?.language);
      matched && setPlayerState({
        ...playerState,
        captionState: {
          prevCaptionIndex: trackIndex,
          selectedCaptionLang: {
            lang: matched.language,
            label: matched.label,
          },
        },
      });
    }
  };
  return (
    <PlayerControlButton
      size="semiLarge"
      icon={captionState === 'off' ? <IconCaptionOff/> : <IconCaptionSwitch/>}
      onClick={() => handleCaptionChange()}
      mr={isMobile ? 0.5 : 2}
    />
  );
};

export default ControlBarCaptionSwitch;
